import { supabase } from "@/integrations/supabase/client";
import { isStaleAssetError, recoverFromStaleAssets } from "@/lib/pwaRecovery";
import { sanitizeLogUrl } from "@/lib/safeUrls";
import { getClientEnv, getClientEnvPayload } from "@/lib/clientEnv";
import { getRuntimeAppVersion } from "@/lib/appVersion";

type Breadcrumb = {
  type: "click" | "navigation" | "fetch" | "console" | "custom";
  message: string;
  timestamp: number;
  data?: Record<string, unknown>;
};

const breadcrumbs: Breadcrumb[] = [];
const MAX_BREADCRUMBS = 20;
const sessionId =
  globalThis.crypto?.randomUUID?.() ??
  Math.random().toString(36).slice(2);

export function addBreadcrumb(b: Omit<Breadcrumb, "timestamp">) {
  breadcrumbs.push({ ...b, timestamp: Date.now() });
  if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();
}

const reportedFingerprints = new Map<string, number>();
let lastReport = 0;
const DEDUPE_WINDOW_MS = 60_000;

export async function reportError(opts: {
  message: string;
  stack?: string;
  error_type?: string;
  source?: string;
  severity?: "info" | "warning" | "error" | "fatal";
  metadata?: Record<string, unknown>;
}) {
  // Soft throttle: max 1 report / 1 second
  const now = Date.now();
  if (now - lastReport < 1000) return;
  lastReport = now;

  // Dedupe within a 60s window — repeated occurrences later still get logged
  const key = `${opts.source ?? ""}::${opts.message.slice(0, 100)}`;
  const lastSeen = reportedFingerprints.get(key) ?? 0;
  if (now - lastSeen < DEDUPE_WINDOW_MS) return;
  reportedFingerprints.set(key, now);

  try {
    const env = getClientEnv();
    await supabase.functions.invoke("log-error", {
      body: {
        message: opts.message,
        stack: opts.stack,
        error_type: opts.error_type,
        source: opts.source,
        severity: opts.severity ?? "error",
        page_url: sanitizeLogUrl(location.href),
        user_action: breadcrumbs[breadcrumbs.length - 1]?.message,
        breadcrumbs: breadcrumbs.slice(),
        session_id: sessionId,
        user_agent: navigator.userAgent,
        device_type: env.device_type,
        viewport: env.viewport,
        app_version: getRuntimeAppVersion(),
        metadata: {
          ...(opts.metadata ?? {}),
          client_env: getClientEnvPayload(),
        },
      },
    });
  } catch {
    // Swallow — never let logger crash the app
  }
}

/**
 * Report a Supabase / PostgREST error (RLS, constraint, network).
 * These don't surface as JS exceptions, so window.onerror never sees them.
 */
export function reportSupabaseError(
  err: unknown,
  context: { source: string; metadata?: Record<string, unknown> },
) {
  if (!err) return;
  const e = err as {
    message?: string;
    code?: string;
    details?: string;
    hint?: string;
    name?: string;
    stack?: string;
  };
  const message = e.message || String(err);
  reportError({
    message,
    stack: e.stack,
    error_type: e.code || e.name || "SupabaseError",
    source: context.source,
    severity: message.toLowerCase().includes("row-level security") ? "fatal" : "error",
    metadata: {
      code: e.code,
      details: e.details,
      hint: e.hint,
      ...context.metadata,
    },
  });
}

let installed = false;
export function installErrorTracking() {
  if (installed) return;
  installed = true;

  // Noise filter: ignore errors that carry no actionable info.
  const isNoise = (msg: string, stack?: string) => {
    const m = (msg || "").toLowerCase();
    if (!m) return true;
    // Cross-origin scripts surface as "Script error." with no stack — useless for debugging.
    if (m === "script error." || m === "script error") return true;
    // Browser extensions / injected scripts
    if (stack && /chrome-extension:|moz-extension:|safari-extension:/i.test(stack)) return true;
    // ResizeObserver noise (benign, fires on layout)
    if (m.includes("resizeobserver loop")) return true;
    // Network aborts from user navigation
    if (m.includes("the operation was aborted") || m.includes("load failed")) return true;
    // Transient edge runtime restarts (503/SUPABASE_EDGE_RUNTIME_ERROR) — not actionable.
    // Especially noisy from track-analytics which fires constantly.
    if (m.includes("supabase_edge_runtime_error")) return true;
    if (m.includes("service is temporarily unavailable")) return true;
    if (m.includes("edge function returned 503") || m.includes("edge function returned 504")) return true;
    if (m.includes("track-analytics")) return true;
    return false;
  };

  window.addEventListener("vite:preloadError", (event) => {
    const preloadEvent = event as Event & { payload?: unknown };
    if (isStaleAssetError(preloadEvent.payload) && recoverFromStaleAssets(preloadEvent.payload)) {
      event.preventDefault();
    }
  });

  // Global JS errors
  window.addEventListener("error", (e) => {
    if (isStaleAssetError(e.message) && recoverFromStaleAssets(e.message)) return;
    if (isNoise(e.message, e.error?.stack)) return;
    reportError({
      message: e.message || "Unknown error",
      stack: e.error?.stack,
      error_type: e.error?.name ?? "Error",
      source: e.filename,
      severity: "error",
    });
  });

  // Unhandled promise rejections
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    const message =
      typeof reason === "string"
        ? reason
        : reason?.message ?? "Unhandled promise rejection";
    if (isStaleAssetError(message) && recoverFromStaleAssets(reason ?? message)) return;
    if (isNoise(message, reason?.stack)) return;
    reportError({
      message,
      stack: reason?.stack,
      error_type: reason?.name ?? "UnhandledRejection",
      severity: "error",
    });
  });

  // Vite lazy-chunk preload failures — recover instead of crashing to blank.
  window.addEventListener("vite:preloadError", (e: Event) => {
    try {
      (e as Event & { preventDefault: () => void }).preventDefault();
    } catch {
      // ignore
    }
    const payload = (e as unknown as { payload?: unknown }).payload;
    recoverFromStaleAssets(payload ?? "vite preload error");
  });

  // Click breadcrumbs
  document.addEventListener(
    "click",
    (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const label =
        t.getAttribute("aria-label") ||
        t.textContent?.trim().slice(0, 60) ||
        t.tagName.toLowerCase();
      addBreadcrumb({ type: "click", message: `Click: ${label}` });
    },
    true,
  );

  // Navigation breadcrumbs
  let lastPath = location.pathname;
  const checkNav = () => {
    if (location.pathname !== lastPath) {
      addBreadcrumb({
        type: "navigation",
        message: `${lastPath} → ${location.pathname}`,
      });
      lastPath = location.pathname;
    }
  };
  window.addEventListener("popstate", checkNav);
  const origPush = history.pushState;
  history.pushState = function (...args) {
    origPush.apply(this, args as Parameters<typeof history.pushState>);
    setTimeout(checkNav, 0);
  };
}
